import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = guest, object = authenticated
    const [authModal, setAuthModal] = useState(null); // null | "register" | "login" | "verify"
    const [postAuth, setPostAuth] = useState(null); // optional continuation run after a verified sign-in

    useEffect(() => {
        api.get("/auth/me")
            .then((res) => setUser(res.data))
            .catch(() => setUser(false));
    }, []);

    const login = async (email, password) => {
        const res = await api.post("/auth/login", { email, password });
        setUser(res.data.user);
        return res.data.user;
    };

    const register = async (payload) => {
        const res = await api.post("/auth/register", payload);
        setUser(res.data.user);
        return res.data.user;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            /* session cleared client-side regardless */
        }
        setUser(false);
    };

    const refreshUser = async () => {
        try {
            const res = await api.get("/auth/me");
            setUser(res.data);
        } catch {
            /* keep current state */
        }
    };

    const updateMarketing = async (consent) => {
        await api.patch("/auth/marketing", { consent });
        setUser((prev) => (prev ? { ...prev, marketing_consent: consent } : prev));
    };

    const openAuth = (mode = "register", onSuccess = null) => {
        setAuthModal(mode);
        setPostAuth(() => onSuccess);
    };
    const closeAuth = () => {
        setAuthModal(null);
        setPostAuth(null);
    };
    // verified sign-in completed: close the modal and resume the pending action (e.g. go to checkout)
    const completeAuth = () => {
        const fn = postAuth;
        setAuthModal(null);
        setPostAuth(null);
        if (fn) fn();
    };

    return (
        <AuthContext.Provider
            value={{ user, authModal, openAuth, closeAuth, completeAuth, login, register, logout, refreshUser, updateMarketing }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}

export function formatApiError(error) {
    const detail = error?.response?.data?.detail;
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
